import React from 'react'
import { AddressAutocomplete } from '../AddressAutocomplete'
import type { PropertyAddressData, PropertyCustomerOption } from './types'

interface PropertyAddressFieldsProps {
  value: PropertyAddressData
  onChange: (value: PropertyAddressData) => void
  customers?: PropertyCustomerOption[]
  hideCustomerSelect?: boolean
  compact?: boolean
}

export function PropertyAddressFields({
  value,
  onChange,
  customers = [],
  hideCustomerSelect,
  compact,
}: PropertyAddressFieldsProps) {
  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const set = (patch: Partial<PropertyAddressData>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!hideCustomerSelect && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
            <select
              required
              value={value.customer_id}
              onChange={(e) => set({ customer_id: e.target.value })}
              className={inputClass}
            >
              <option value="">Select a customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company_name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Property Name *</label>
          <input
            type="text"
            required
            value={value.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g., Main Office Building"
            className={inputClass}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
          <AddressAutocomplete
            value={value.address}
            onChange={(address) => set({ address })}
            onAddressSelect={(addressData) =>
              set({
                address: addressData.address,
                city: addressData.city,
                state: addressData.state,
                zip: addressData.zip,
              })
            }
            placeholder="Enter full address"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Apt/Suite # <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            value={value.address_line_2}
            onChange={(e) => set({ address_line_2: e.target.value })}
            placeholder="Apartment, Suite, Unit, etc."
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
          <input
            type="text"
            required
            value={value.city}
            onChange={(e) => set({ city: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
          <input
            type="text"
            required
            value={value.state}
            onChange={(e) => set({ state: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
          <input
            type="text"
            required
            value={value.zip}
            onChange={(e) => set({ zip: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>
    </div>
  )
}
